<template>
  <report-page
    :title="name"
    :buttons-disabled="buttonsDisabled"
    :history-filter="historyFilter"
    :selectedWorkspaceUUID="selectedWorkspace"
    :data-to-send="dataToSend"
  >
    <template v-slot:reportForm>
      <report-field :field-name="$tc('Workspace', 1)">
        <treeselect
          v-model="selectedWorkspace"
          :options="workspaceOptions"
          :disabled="
            !hasPermissionToCreateExport ||
              workspaceSelectionDisabled ||
              !userHasManagementPermission('workspace', 'list')
          "
          disableFuzzyMatching
        />
      </report-field>
      <report-field :field-name="$t(reportParameters[0].label)">
        <treeselect
          :value="reportParameters[0].value"
          @input="updateOpticalEquipmentFields"
          :options="networkStartingPointOptions"
          :load-options="getNetworkStartingPointOptions"
          :disabled="
            !hasPermissionToCreateExport ||
              startingPointFieldDisabled ||
              !userHasPermissionToList('technical_local')
          "
          :disable-branch-nodes="true"
          disableFuzzyMatching
        />
      </report-field>
      <report-field :field-name="$tc(reportParameters[1].label, 2)">
        <treeselect
          v-model="reportParameters[1].value"
          :options="centralOfficeOptions"
          :multiple="true"
          value-consists-of="LEAF_PRIORITY"
          :default-expand-level="1"
          :disabled="
            !hasPermissionToCreateExport ||
              opticalEquipmentFieldsDisabled ||
              !userHasPermissionToList('technical_local')
          "
          disableFuzzyMatching
        />
      </report-field>
      <report-field :field-name="$tc(reportParameters[2].label, 2)">
        <treeselect
          v-model="reportParameters[2].value"
          :options="primaryNodeOptions"
          :multiple="true"
          value-consists-of="LEAF_PRIORITY"
          :default-expand-level="1"
          :disabled="
            !hasPermissionToCreateExport ||
              opticalEquipmentFieldsDisabled ||
              !userHasPermissionToList('technical_local')
          "
          disableFuzzyMatching
        />
      </report-field>
      <report-field :field-name="$tc(reportParameters[3].label, 2)">
        <treeselect
          v-model="reportParameters[3].value"
          :options="closureOptions"
          :multiple="true"
          value-consists-of="LEAF_PRIORITY"
          :default-expand-level="1"
          :disabled="
            !hasPermissionToCreateExport ||
              opticalEquipmentFieldsDisabled ||
              !userHasPermissionToList('closure')
          "
          disableFuzzyMatching
        />
      </report-field>
      <report-field :field-name="$tc(reportParameters[4].label, 2)">
        <treeselect
          v-model="reportParameters[4].value"
          :options="buildingOptions"
          :multiple="true"
          value-consists-of="LEAF_PRIORITY"
          :default-expand-level="1"
          :disabled="
            !hasPermissionToCreateExport ||
              opticalEquipmentFieldsDisabled ||
              !userHasPermissionToList('building')
          "
          disableFuzzyMatching
        />
      </report-field>
    </template>
  </report-page>
</template>

<script>
import ReportPage from "../ReportPage";
import ReportField from "../ReportField";
import { homeReportPageMixin } from "@/mixins/homeReportPageMixin";

export default {
  name: "FibersPathsReports",

  components: {
    ReportField,
    ReportPage
  },

  mixins: [homeReportPageMixin],

  props: {
    name: {
      type: String,
      required: true
    },
    version: {
      type: String
    }
  },

  data() {
    return {
      reportParameters: [
        {
          label: "NetworkStartingPoint",
          key: "technical_local",
          type: "text",
          value: undefined
        },
        {
          label: "CentralOffice",
          key: "central_offices",
          type: "array",
          value: []
        },
        {
          label: "PrimaryNode",
          key: "primary_nodes",
          type: "array",
          value: []
        },
        {
          label: "Closure",
          key: "closures",
          type: "array",
          value: []
        },
        {
          label: "Building",
          key: "buildings",
          type: "array",
          value: []
        }
      ],

      centralOfficeOptions: [],
      primaryNodeOptions: [],
      closureOptions: [],
      buildingOptions: [],

      historyFilter: "fibers_path"
    };
  },

  computed: {
    startingPointFieldDisabled() {
      return this.selectedWorkspace === undefined;
    },
    opticalEquipmentFieldsDisabled() {
      return this.reportParameters[0].value === undefined;
    },
    buttonsDisabled() {
      return (
        this.reportParameters[1].value.length === 0 &&
        this.reportParameters[2].value.length === 0 &&
        this.reportParameters[3].value.length === 0 &&
        this.reportParameters[4].value.length === 0
      );
    },
    dataToSend() {
      return {
        type: this.historyFilter,
        version: this.version,
        objects: {
          workspace_uuid: this.selectedWorkspace,
          object: {
            uuid: this.reportParameters[0].value,
            object_type: "technical_local"
          }
        }
      };
    }
  },
  watch: {
    selectedWorkspace() {
      this.resetReportParameterValues(this.reportParameters, 0);
      this.networkStartingPointOptions = null;
    }
  },

  mounted() {
    this.setWorkspaceField();
  },

  methods: {
    loadClosureOptions() {
      this.$store
        .dispatch("GET_ALL_OBJECTS_IN_GIVEN_WORKSPACE", {
          endpoint: "closures",
          workspaceUUID: this.selectedWorkspace,
          technicalType: undefined
        })
        .then(closures => {
          let closureOptions = this.optionListNormalizer(closures, "cl");
          this.closureOptions = this.includeInAllOption(closureOptions);
        })
        .catch(error => console.log(error));
    },
    loadBuildingOptions() {
      this.$store
        .dispatch("GET_ALL_OBJECTS_IN_GIVEN_WORKSPACE", {
          endpoint: "buildings",
          workspaceUUID: this.selectedWorkspace,
          technicalType: undefined
        })
        .then(buildings => {
          let buildingOptions = this.optionListNormalizer(buildings, "bd");
          this.buildingOptions = this.includeInAllOption(buildingOptions);
        })
        .catch(error => console.log(error));
    },
    updateOpticalEquipmentFields(e) {
      if (this.reportParameters[0].value !== e) {
        this.resetReportParameterValues(this.reportParameters, 1);
      }
      if (e !== undefined) {
        let centralOfficeOptions = this.getWorkspaceCentralOfficesOptions(
          this.workspaceTechnicalSitesWithLocals
        );
        this.centralOfficeOptions = this.includeInAllOption(
          centralOfficeOptions
        );
        let primaryNodeOptions = this.getWorkspacePrimaryNodesOptions(
          this.workspaceTechnicalSitesWithLocals
        );
        this.primaryNodeOptions = this.includeInAllOption(primaryNodeOptions);
        this.loadClosureOptions();
        this.loadBuildingOptions();
      }
      this.reportParameters[0].value = e;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/Home/Content/Tabs/Reports/ReportsPages/FibersPaths.vue